import type { Column } from '@vd/types';
import styles from '../Settings.module.css';
import { BigSwitch } from '../../../features/settings/BigSwitch';
import { IntakeBoardDrawing } from './IntakeBoardDrawing';

/**
 * Project settings, Intake tab (live feedback 2026-08-28).
 *
 * Deliberately its own tab rather than a section inside Widget or
 * Columns: this is the one place that explains WHAT WILL HAPPEN when the
 * toggle flips, and the other tabs are already dense. The board chip on
 * the intake column deep-links here; the Columns tab keeps a compact
 * copy of the same toggle because that is where the column itself is
 * managed. Both render the same server state, so they can never
 * disagree.
 */
export function IntakeTab({
  intakeEnabled, onToggleIntake, columnsDirty, serverIntakeColumnName, toggling, columns,
}: {
  intakeEnabled: boolean;
  onToggleIntake: (enabled: boolean) => Promise<void>;
  /** Unsaved edits on the Columns tab. Enabling intake creates a column
   *  server-side, and saving stale column state afterwards would delete
   *  it - the same hazard the Columns tab guards against, so this tab
   *  disables the toggle under the same condition. */
  columnsDirty: boolean;
  serverIntakeColumnName: string | null;
  toggling: boolean;
  /** The project's columns as saved, for the drawing of its board (TOADO-584); undefined while they load. */
  columns: Column[] | undefined;
}) {
  return (
    <>
      <h2 className={styles.h1}>Bug intake</h2>
      <p className={styles.sub}>
        A holding column for reports from people outside your team, so a person can judge them
        before they reach the board your team works from.
      </p>
      <IntakeBoardDrawing
        columns={columns}
        intakeEnabled={intakeEnabled}
        intakeColumnName={serverIntakeColumnName}
      />

      <section className={styles.section}>
        {/* The large Off/On switch (Jason, 10 Oct), named by the line beside it. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <BigSwitch
            labelledBy="intake-route-title"
            checked={intakeEnabled}
            disabled={toggling || columnsDirty}
            onChange={(on) => { void onToggleIntake(on); }}
          />
          <span style={{ paddingTop: 6 }}>
            <strong id="intake-route-title">Route incoming widget reports to a Bug intake column</strong>
            {columnsDirty && (
              <span className={styles.sub} style={{ display: 'block', marginBottom: 0 }}>
                Save or discard your changes on the Columns tab to change this setting.
              </span>
            )}
            {intakeEnabled && serverIntakeColumnName !== null && (
              <span className={styles.sub} style={{ display: 'block', marginBottom: 0 }}>
                Reports currently land in <strong>{serverIntakeColumnName}</strong>.
              </span>
            )}
            {intakeEnabled && serverIntakeColumnName === null && (
              <span style={{ display: 'block', color: 'var(--danger)' }}>
                Bug intake is enabled but its column was deleted, so reports are falling back to the
                first work column. Turn the toggle off and on again to recreate it.
              </span>
            )}
          </span>
        </div>
      </section>

      <section className={styles.section}>
        <h3 style={{ fontSize: 'var(--fs-md)', marginBottom: 8 }}>What happens when this is on</h3>
        <ul className={styles.sub} style={{ paddingLeft: 18, display: 'grid', gap: 6, marginBottom: 0 }}>
          <li>
            A <strong>Bug intake</strong> column is added at the left of the board (or an existing
            column named "Bug intake" is adopted). It works like any other column: rename it, move
            it, recolor it. Routing follows the column itself, not its name or position.
          </li>
          <li>
            <strong>Anonymous reports</strong> from the embedded widget and public report links land
            in that column instead of the first column.
          </li>
          <li>
            <strong>Your team's work never lands there.</strong> Extension captures, manually
            created tickets, and captures a teammate claims from the widget all go to the first
            work column. Only reports from strangers wait for judgement.
          </li>
          <li>
            Triage from the board: select several intake tickets, then archive the noise or move
            the real ones to a working column. Reports count against your ticket quota from the
            moment they arrive, so archiving junk promptly matters on a busy public widget.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h3 style={{ fontSize: 'var(--fs-md)', marginBottom: 8 }}>What happens when you turn it off</h3>
        <ul className={styles.sub} style={{ paddingLeft: 18, display: 'grid', gap: 6, marginBottom: 0 }}>
          <li>New reports go back to the first work column, exactly as before.</li>
          <li>
            An <strong>empty</strong> intake column is removed. A column still holding tickets
            stays where it is with everything in it - nothing is ever moved for you - and turning
            the toggle back on adopts it again.
          </li>
        </ul>
      </section>
    </>
  );
}
